import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileWarning, RefreshCw, ShieldCheck, Sparkles } from 'lucide-react';
import clsx from 'clsx';
import { apiClient, ApiError } from '@/lib/api';
import type { CallRecordDetail, QcResult } from '@/lib/types';
import { isAdminRole, useAuthStore } from '@/lib/auth-store';
import { resolveCustomerNameDisplay } from '@/lib/customer-name';
import { RevealPhoneCell } from '@/components/calls/reveal-phone';
import {
  QC_NO_FINDINGS_NOTE,
  QC_REFORMAT_FALLBACK_NOTE,
  formatTranscriptForDisplay,
  resolveQcAnalysisState,
  resolveReformatView,
  resolveTranscriptView,
  shouldPollQc,
  type QcAnalysisState,
} from '@/lib/qc-report-view';
import { Card } from '@/components/ui/card';
import { CallStatusBadge, QcResultBadge } from '@/components/ui/status-badge';
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from '@/components/ui/empty-state';
import {
  formatDateTime,
  formatDuration,
  formatScore,
} from '@/lib/format';

const SEVERITY_STYLES: Record<string, string> = {
  high: 'bg-rose-50 text-rose-700 ring-rose-200',
  medium: 'bg-amber-50 text-amber-700 ring-amber-200',
  low: 'bg-sky-50 text-sky-700 ring-sky-200',
};

export function CallDetailPage() {
  const params = useParams();
  const callId = params.id ?? '';
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  const query = useQuery({
    queryKey: ['calls', 'detail', callId],
    enabled: !!callId,
    queryFn: () => apiClient.get<CallRecordDetail>(`/calls/${callId}`),
    // Issue #217：重跑 AI 质检是异步流水线，提交后到落库有窗口期。当处理态为
    // PENDING / PROCESSING 时按 2.5s 轮询，让管理员「不刷新页面也能看到任务状态
    // 与最终结果」；落到终态（COMPLETED / FAILED）即停止轮询。
    refetchInterval: (q) => {
      const data = q.state.data as CallRecordDetail | undefined;
      return shouldPollQc(data?.qcSummary?.processingStatus) ? 2500 : false;
    },
  });

  const [reviewComment, setReviewComment] = useState('');
  const [reviewDecision, setReviewDecision] = useState<QcResult>('PASS');
  const [reviewError, setReviewError] = useState<string | null>(null);
  // Issue #326：复核备注必须大于 10 个字（按可见字符计），与后端 REVIEW_COMMENT_MIN_CHARS 同口径。
  const reviewCommentValid = Array.from(reviewComment.trim()).length > 10;
  // Issue #217：重跑按钮的成功 / 失败提示（含 HTTP 状态码 + 非 JSON 错误页处理）。
  const [rerunNotice, setRerunNotice] = useState<
    { kind: 'success' | 'error'; text: string } | null
  >(null);

  const reviewMutation = useMutation({
    mutationFn: async () => {
      // Issue #326：不再硬编码 reviewStatus——由后端按 qcResult 派生（合规通过/不合规→REVIEWED，
      // 暂缓/转上级→IN_REVIEW），与移动端复核口径一致。复核备注须大于 10 个字（后端 DTO 强制）。
      return apiClient.post<unknown>(`/reports/calls/${callId}/review`, {
        qcResult: reviewDecision,
        comment: reviewComment.trim(),
      });
    },
    onSuccess: async () => {
      setReviewError(null);
      setReviewComment('');
      await queryClient.invalidateQueries({ queryKey: ['calls', 'detail', callId] });
    },
    onError: (err) => {
      setReviewError(err instanceof ApiError ? err.message : '复核失败，请重试');
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: () =>
      apiClient.post<unknown>(`/reports/calls/${callId}/generate`, {
        context: 'admin manual rerun',
        reason: '管理员手动触发',
      }),
    onSuccess: async () => {
      setRerunNotice({
        kind: 'success',
        text: '已提交重跑请求，AI 正在重新评分，结果会自动刷新…',
      });
      await queryClient.invalidateQueries({ queryKey: ['calls', 'detail', callId] });
    },
    onError: (err) => {
      // ApiError.message 在非 JSON / HTML 错误页时已带 HTTP 状态码，这里再显式拼上 status。
      setRerunNotice({
        kind: 'error',
        text:
          err instanceof ApiError
            ? `重跑失败（HTTP ${err.status}）：${err.message}`
            : err instanceof Error
              ? err.message
              : '重跑失败，请稍后重试',
      });
    },
  });

  const detail = query.data;
  const qc = detail?.qcSummary ?? null;
  const dimensions = qc?.dimensions ?? [];

  // Issue #217：四个 AI 分析版块的展示态——区分「进行中 / 失败 / AI 确认无内容 / 正常」。
  const processingStatus = qc?.processingStatus;
  const failureReason = qc?.failureReason ?? null;
  const dimensionState = resolveQcAnalysisState(processingStatus, failureReason, dimensions.length > 0);
  const problemState = resolveQcAnalysisState(
    processingStatus,
    failureReason,
    (qc?.problemPoints?.length ?? 0) > 0,
  );
  const intentState = resolveQcAnalysisState(
    processingStatus,
    failureReason,
    typeof qc?.customerIntent === 'string' && qc.customerIntent.trim().length > 0,
  );
  const scriptsState = resolveQcAnalysisState(
    processingStatus,
    failureReason,
    (qc?.suggestedScripts?.length ?? 0) > 0,
  );

  const overall = useMemo(() => {
    if (!detail) return null;
    return detail.qcScore ?? detail.aiPredictedScore ?? null;
  }, [detail]);

  const canReview = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN' || user?.role === 'MANAGER';

  // Issue #215 / #391 / #397：完整通话文本仅 ADMIN / SUPER_ADMIN 可见。前端仍做角色兜底，
  // 防止后端误下发时泄露。「AI纠错/重排版」展示 AI文本纠错最终文本（formattedTranscript，直接
  // 镜像 correctedTranscript）；历史未回填的记录回退展示内容一致的 correctedTranscript 并标注。
  const reformatView = resolveReformatView(
    user?.role,
    qc?.formattedTranscript,
    qc?.correctedTranscript,
  );
  const rawTranscriptView = resolveTranscriptView(
    user?.role,
    qc?.rawTranscript ?? qc?.transcript,
  );

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-center justify-between">
        <div>
          <Link
            to="/calls"
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            返回通话记录
          </Link>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">
            通话质检报告
          </h1>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            type="button"
            onClick={() => regenerateMutation.mutate()}
            disabled={regenerateMutation.isPending}
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-60"
          >
            <RefreshCw
              className={clsx(
                'h-4 w-4',
                (regenerateMutation.isPending || (qc && shouldPollQc(qc.processingStatus))) &&
                  'animate-spin',
              )}
            />
            重跑 AI 质检
          </button>
          {qc ? <QcStatusBadge state={problemState} mock={qc.mock} /> : null}
        </div>
      </header>

      {rerunNotice ? (
        <div
          role="status"
          className={clsx(
            'rounded-md border px-3 py-2 text-sm',
            rerunNotice.kind === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'border-rose-200 bg-rose-50 text-rose-700',
          )}
        >
          {rerunNotice.text}
        </div>
      ) : null}

      {query.isLoading ? (
        <Card>
          <LoadingState label="加载通话详情…" />
        </Card>
      ) : query.isError ? (
        <Card>
          <ErrorState
            title="加载详情失败"
            description={
              query.error instanceof ApiError && query.error.status === 404
                ? '通话不存在或您没有权限查看'
                : query.error instanceof Error
                  ? query.error.message
                  : '请稍后重试'
            }
            retry={() => query.refetch()}
          />
        </Card>
      ) : detail ? (
        <>
          <section className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <Card title="通话概况" className="xl:col-span-2">
              <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
                {/* Issue #178：ADMIN/SUPER_ADMIN 见完整客户名，其余脱敏。 */}
                <Field label="客户" value={resolveCustomerNameDisplay(detail)} />
                {/* Issue #228：完整手机号需二次密码（30 分钟会话放行）；MANAGER 仅见脱敏。 */}
                <Field
                  label="手机号"
                  value={
                    isAdminRole(user?.role) ? (
                      <RevealPhoneCell callId={detail.id} phoneMasked={detail.phoneMasked} />
                    ) : (
                      detail.phoneMasked || '—'
                    )
                  }
                />
                <Field label="坐席" value={detail.employeeName ?? '—'} />
                <Field label="部门" value={detail.employeeDeptName ?? '—'} />
                <Field label="岗位" value={detail.employeePosition ?? '—'} />
                <Field
                  label="开始时间"
                  value={formatDateTime(detail.startedAt)}
                />
                <Field
                  label="通话时长"
                  value={formatDuration(detail.duration)}
                />
                <Field
                  label="状态"
                  value={<CallStatusBadge status={detail.status} />}
                />
              </div>
            </Card>
            <Card title="综合评分">
              <div className="flex flex-col items-center gap-3 py-4">
                <div className="text-5xl font-semibold text-brand-600">
                  {overall === null ? '--' : formatScore(overall)}
                </div>
                <div className="text-xs text-slate-500">AI 综合评分（满分 100）</div>
                <QcResultBadge result={qc?.qcResult ?? null} />
                {qc?.reviewedAt ? (
                  <div className="text-[11px] text-slate-400">
                    复核于 {formatDateTime(qc.reviewedAt)}
                  </div>
                ) : null}
              </div>
            </Card>
          </section>

          <section className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <Card title="维度得分" className="xl:col-span-2">
              {dimensionState.kind !== 'ready' ? (
                <QcSectionPlaceholder
                  state={dimensionState}
                  emptyTitle="尚未生成维度评分"
                  emptyDescription="本次 AI 评分未返回维度得分，可点击右上角“重跑 AI 质检”。"
                />
              ) : (
                <ul className="grid gap-3 md:grid-cols-2">
                  {dimensions.map((dim) => (
                    <li
                      key={dim.name}
                      className="rounded-xl border border-slate-200 bg-slate-50 p-3"
                    >
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-slate-800">
                          {dim.displayName || dim.name}
                        </span>
                        <span className="font-semibold text-brand-600">
                          {formatScore(dim.score)}
                        </span>
                      </div>
                      <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
                        <div
                          className="h-full rounded-full bg-brand-500"
                          style={{ width: `${Math.min(100, Math.max(0, dim.score))}%` }}
                        />
                      </div>
                      {dim.feedback ? (
                        <p className="mt-2 text-xs text-slate-600">{dim.feedback}</p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card title="客户意向 / 建议话术" description="AI 总结的客户意向 + 推荐话术">
              <div className="space-y-3 text-sm text-slate-700">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    客户意向
                  </div>
                  <div className="mt-1 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
                    {intentState.kind === 'ready'
                      ? qc?.customerIntent
                      : describeQcState(intentState, 'AI 未总结出明确客户意向')}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    建议话术
                  </div>
                  {scriptsState.kind !== 'ready' ? (
                    <div className="mt-1 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
                      {/* Issue #227：AI 跑完但无建议时显示统一备注，取代旧的空态文案。 */}
                      {describeQcState(scriptsState, QC_NO_FINDINGS_NOTE)}
                    </div>
                  ) : (
                    <ul className="mt-1 space-y-2">
                      {qc!.suggestedScripts.map((script, idx) => (
                        <li
                          key={idx}
                          className="rounded-lg bg-emerald-50 p-3 text-xs text-emerald-800"
                        >
                          {script}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </Card>
          </section>

          <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <Card
              title="问题点"
              description="AI 标记的疑似违规 / 风险时刻"
              action={<FileWarning className="h-4 w-4 text-amber-500" />}
            >
              {problemState.kind !== 'ready' ? (
                <QcSectionPlaceholder
                  state={problemState}
                  emptyTitle={QC_NO_FINDINGS_NOTE}
                  emptyDescription="AI 质检已完成，本次通话未检出需提示的问题点。"
                />
              ) : (
                <ul className="space-y-3">
                  {qc!.problemPoints.map((point, idx) => (
                    <li
                      key={idx}
                      className="rounded-xl border border-slate-200 bg-white p-3 text-sm"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span className="font-medium text-slate-800">
                          {point.issue}
                        </span>
                        {point.severity ? (
                          <span
                            className={clsx(
                              'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] ring-1 ring-inset',
                              SEVERITY_STYLES[point.severity] ?? 'bg-slate-50 text-slate-500 ring-slate-200',
                            )}
                          >
                            {point.severity.toUpperCase()}
                          </span>
                        ) : null}
                      </div>
                      {point.suggestion ? (
                        <p className="mt-1 text-xs text-slate-500">
                          建议：{point.suggestion}
                        </p>
                      ) : null}
                      {typeof point.timestampMs === 'number' ? (
                        <p className="mt-1 text-[11px] text-slate-400">
                          时间戳：{(point.timestampMs / 1000).toFixed(1)} s
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card
              title="复核操作"
              description="ADMIN/MANAGER 可在此提交人工复核结论"
              action={<ShieldCheck className="h-4 w-4 text-emerald-500" />}
            >
              {!canReview ? (
                <EmptyState
                  title="当前角色无权复核"
                  description="只有主管及以上角色可提交复核结论。"
                />
              ) : (
                <form
                  className="space-y-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    reviewMutation.mutate();
                  }}
                >
                  <label className="block text-xs text-slate-600">
                    <span className="mb-1 block">复核结论</span>
                    <select
                      value={reviewDecision}
                      onChange={(e) => setReviewDecision(e.target.value as QcResult)}
                      className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                    >
                      <option value="PASS">合规通过</option>
                      <option value="FAIL">不合规</option>
                      <option value="PENDING">暂缓 / 转上级</option>
                    </select>
                  </label>
                  <label className="block text-xs text-slate-600">
                    <span className="mb-1 block">复核备注</span>
                    <textarea
                      rows={4}
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      placeholder="请输入复核意见（需大于 10 个字）"
                      className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                    />
                  </label>
                  {/* Issue #326：备注须大于 10 个字，前端提交前校验 + 后端 DTO 强制。 */}
                  {!reviewCommentValid && reviewComment.trim().length > 0 ? (
                    <div className="text-xs text-amber-600">复核备注需大于 10 个字。</div>
                  ) : null}
                  {reviewError ? (
                    <div className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs text-rose-700">
                      {reviewError}
                    </div>
                  ) : null}
                  <button
                    type="submit"
                    disabled={reviewMutation.isPending || !reviewCommentValid}
                    className="inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
                  >
                    {reviewMutation.isPending ? '提交中…' : '提交复核'}
                  </button>
                  {qc?.reviewComment ? (
                    <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
                      <div className="font-medium text-slate-500">上次复核备注</div>
                      <div className="mt-1">{qc.reviewComment}</div>
                    </div>
                  ) : null}
                </form>
              )}
            </Card>
          </section>

          <Card
            title="AI纠错/重排版"
            description="展示管理端「AI文本纠错」的最终文本（同一次纠错已含书面化重排，不改变事实）。原始 ASR 证据保留在下方“通话转写”。"
            action={<Sparkles className="h-4 w-4 text-sky-500" />}
          >
            {reformatView.kind === 'forbidden' ? (
              <EmptyState
                title="当前角色无权查看 AI文本纠错文本"
                description="完整通话文本仍属敏感数据，仅对 ADMIN / SUPER_ADMIN 开放。"
              />
            ) : reformatView.kind === 'empty' ? (
              <EmptyState
                title="暂无 AI文本纠错文本"
                description="历史记录未保留 AI文本纠错审计，或本次通话尚未完成 AI 文本处理。"
              />
            ) : (
              <div className="space-y-2">
                {reformatView.kind === 'fallback' ? (
                  <p className="text-[11px] font-medium text-amber-600">
                    {QC_REFORMAT_FALLBACK_NOTE}
                  </p>
                ) : (
                  <p className="text-[11px] text-slate-400">
                    以下为管理端「AI文本纠错」的最终文本；不改变原始语义与事实。
                    {qc?.formatModel ? `　模型：${qc.formatModel}` : ''}
                    {qc?.formattedAt
                      ? `　生成于 ${formatDateTime(qc.formattedAt)}`
                      : ''}
                  </p>
                )}
                <div className="max-h-96 overflow-y-auto whitespace-pre-wrap rounded-lg border border-sky-100 bg-sky-50/70 p-4 text-sm leading-relaxed text-slate-700">
                  {formatTranscriptForDisplay(reformatView.text)}
                </div>
              </div>
            )}
          </Card>

          <Card
            title="通话转写"
            description="原始 ASR 转写文本属敏感数据，仅 ADMIN / SUPER_ADMIN 可查看完整原文。"
            action={<ShieldCheck className="h-4 w-4 text-emerald-500" />}
          >
            {rawTranscriptView.kind === 'forbidden' ? (
              <EmptyState
                title="当前角色无权查看通话转写"
                description="ASR 原文仅对 ADMIN / SUPER_ADMIN 开放。如需复核可使用上方“维度得分 / 问题点”面板。"
              />
            ) : rawTranscriptView.kind === 'empty' ? (
              <EmptyState
                title="本次通话尚未生成 ASR 文本"
                description="ASR 完成后会回填转写原文；可点击右上角“重跑 AI 质检”。"
              />
            ) : (
              <div className="space-y-2">
                <p className="text-[11px] text-slate-400">
                  以下为 ASR 转写原文（含客户 PII），请勿截图外传；查看行为受后端 RBAC 限制。
                </p>
                <div className="max-h-96 overflow-y-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-700">
                  {rawTranscriptView.text}
                </div>
              </div>
            )}
          </Card>
        </>
      ) : null}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-slate-900">{value}</div>
    </div>
  );
}

/**
 * Issue #217：AI 分析版块的非「ready」占位渲染。把后端处理态映射到 4 类提示，
 * 关键是把「AI 确认无内容」(empty) 与「失败 / 未生成」(failed) 显式区分开。
 */
function QcSectionPlaceholder({
  state,
  emptyTitle,
  emptyDescription,
}: {
  state: QcAnalysisState;
  emptyTitle: string;
  emptyDescription: string;
}) {
  switch (state.kind) {
    case 'pending':
      return (
        <EmptyState
          title="尚未生成"
          description="点击右上角“重跑 AI 质检”开始评分。"
        />
      );
    case 'processing':
      return <LoadingState label="AI 质检进行中，请稍候，结果会自动刷新…" />;
    case 'failed':
      return (
        <ErrorState
          title="AI 质检失败"
          description={state.reason ?? '评分流水线返回失败，可点击右上角“重跑 AI 质检”重试。'}
        />
      );
    case 'empty':
      return <EmptyState title={emptyTitle} description={emptyDescription} />;
    default:
      return null;
  }
}

/** Issue #217：把非 ready 状态映射为一行内联提示文案（供客户意向 / 建议话术等单行版块用）。 */
function describeQcState(state: QcAnalysisState, emptyText: string): string {
  switch (state.kind) {
    case 'pending':
      return '尚未生成';
    case 'processing':
      return 'AI 质检进行中…';
    case 'failed':
      return `质检失败：${state.reason ?? '请重跑质检'}`;
    case 'empty':
      return emptyText;
    default:
      return '';
  }
}

/** Issue #217：报告头部的处理状态徽标，让管理员一眼看到重跑任务态 / 示例数据标记。 */
function QcStatusBadge({ state, mock }: { state: QcAnalysisState; mock: boolean }) {
  const map: Record<QcAnalysisState['kind'], { text: string; cls: string }> = {
    pending: { text: '待评分', cls: 'bg-slate-50 text-slate-500 ring-slate-200' },
    processing: { text: '质检进行中', cls: 'bg-sky-50 text-sky-700 ring-sky-200' },
    failed: { text: '质检失败', cls: 'bg-rose-50 text-rose-700 ring-rose-200' },
    empty: { text: '已完成', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
    ready: { text: '已完成', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  };
  const badge = map[state.kind];
  return (
    <div className="flex items-center gap-2">
      <span
        className={clsx(
          'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] ring-1 ring-inset',
          badge.cls,
        )}
      >
        {badge.text}
      </span>
      {mock ? (
        <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700 ring-1 ring-inset ring-amber-200">
          示例数据
        </span>
      ) : null}
    </div>
  );
}
